import React, { useContext, useMemo } from 'react';

/**
 * The "why am I here" marker for search: wrap matches of the active query in
 * a <mark> so the thing that brought you to a page is visible on it.
 *
 * Lived in features/agent-analytics/components/payloadEntityLinks.js first
 * (the in-run search); promoted to shared when global search started opening
 * users, events, issues and capabilities too. payloadEntityLinks re-exports
 * these, so its consumers didn't move.
 *
 * WHAT CHANGED AND WHY. This used to mark occurrences of one literal string —
 * the raw query, matched with indexOf. That works for "click a keyword hit"
 * and for nothing else:
 *
 *   • A semantic hit is a passage that means the same thing as the query
 *     without containing it. Marking the query text found zero occurrences, so
 *     the user was dropped into a span with nothing highlighted and no idea
 *     which part had matched.
 *   • "failing users" marked nothing in "the user failed" — a search that
 *     returns a result you then cannot see the reason for is worse than one
 *     that returns nothing.
 *
 * So the context now carries what actually matched: word stems, and the exact
 * passage the backend located. Stems mark whole words ("fail" marks "failed",
 * "failing", "failure"), and the passage marks the specific sentence that
 * caused the hit, as the spotlight.
 *
 * The context value stays back-compatible with a plain string, because global
 * search, the sidebar, capabilities and user detail all still pass one.
 */
export const HighlightContext = React.createContext('');

const MARK_BASE = 'rounded-sm px-px';

function escapeRe(s) {
  return String(s).replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

/**
 * Match a passage tolerantly across whitespace.
 *
 * The passage is located server-side in the stored payload, but it is matched
 * here against what the payload tree renders — and those differ in whitespace
 * more often than not (JSON escapes, wrapped prose, collapsed indentation).
 * An exact substring match therefore fails silently and the spotlight simply
 * never appears, which is indistinguishable from "no match found". Treating
 * any run of whitespace as equivalent removes that failure mode.
 */
function passagePattern(passage) {
  return String(passage)
    .trim()
    .split(/\s+/)
    .map(escapeRe)
    .join('\\s+');
}

/**
 * Normalise whatever a provider passed into { terms, passage }.
 * A bare string is treated as a single literal term, exactly as before.
 */
export function normaliseHighlight(value) {
  if (!value) return null;
  if (typeof value === 'string') {
    const t = value.trim();
    return t ? { terms: [t], passage: '', whole: false } : null;
  }
  const terms = (value.terms || []).map((t) => String(t).trim()).filter(Boolean);
  const passage = String(value.passage || '').trim();
  if (!terms.length && !passage) return null;
  // `whole: true` means the terms are stems and should mark the whole word
  // they sit in, so clicking a hit for "fail" lights up "failed" rather than
  // four letters of it.
  return { terms, passage, whole: value.whole !== false };
}

/**
 * One regex for every term, longest first so "authentication" wins over
 * "auth" and the marks never overlap.
 */
function buildMatcher(hl) {
  if (!hl) return null;
  const parts = [];
  // The passage goes first: it is the most specific thing we know, and when it
  // matches we want the spotlight on it rather than on a word inside it.
  if (hl.passage) parts.push({ re: passagePattern(hl.passage), primary: true });
  const sorted = [...hl.terms].sort((a, b) => b.length - a.length);
  for (const t of sorted) {
    const body = escapeRe(t);
    parts.push({ re: hl.whole ? `[\\p{L}\\p{N}_]*${body}[\\p{L}\\p{N}_]*` : body, primary: false });
  }
  if (!parts.length) return null;
  try {
    return {
      re: new RegExp(parts.map((p) => `(${p.re})`).join('|'), 'giu'),
      primaryGroups: parts.map((p) => p.primary),
    };
  } catch {
    return null;
  }
}

/**
 * Wrap every match of the active query in a <mark>.
 *
 * The passage match also carries data-spotlight, which is what the run detail
 * panel scrolls to and pulses — so "take me to the result" lands on the
 * matching sentence rather than on the first marked word on the page.
 */
export function HighlightText({ text }) {
  const raw = useContext(HighlightContext);
  const hl = useMemo(() => normaliseHighlight(raw), [raw]);
  const matcher = useMemo(() => buildMatcher(hl), [hl]);
  const s = String(text ?? '');
  if (!matcher || !s) return s;

  const out = [];
  let last = 0;
  let n = 0;
  matcher.re.lastIndex = 0;
  let m = matcher.re.exec(s);
  while (m !== null) {
    // A zero-length match would spin forever; step past it.
    if (m[0] === '') { matcher.re.lastIndex += 1; m = matcher.re.exec(s); continue; }
    const groupIdx = m.slice(1).findIndex((g) => g !== undefined);
    const isPrimary = groupIdx >= 0 && matcher.primaryGroups[groupIdx];
    if (m.index > last) out.push(s.slice(last, m.index));
    out.push(
      <mark
        key={`hl-${n}`}
        data-spotlight={isPrimary ? 'true' : undefined}
        className={MARK_BASE}
        style={isPrimary
          ? {
            backgroundColor: 'var(--brand-primary-subtle, rgba(250,204,21,0.5))',
            boxShadow: '0 0 0 2px var(--brand-primary, rgba(234,179,8,0.9))',
            color: 'inherit',
          }
          : { backgroundColor: 'var(--feedback-warning-bg, rgba(250,204,21,0.35))', color: 'inherit' }}
      >
        {m[0]}
      </mark>,
    );
    last = m.index + m[0].length;
    n += 1;
    m = matcher.re.exec(s);
  }
  if (!n) return s;
  if (last < s.length) out.push(s.slice(last));
  return <>{out}</>;
}
